import React, { useContext, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type Order from '../../Models/OrderModel';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { DateInput, ErrorMessage, LoadingButton } from 'shouldve-been-mui';

type PostponeOrderDialogProps = {
    order: Order,
    controller: boolean,
    onClose: (isPostponed: boolean) => void,
};

function PostponeOrderDialog(props: Readonly<PostponeOrderDialogProps>): JSX.Element {

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const [postponedToDate, setPostponedToDate] = useState<Date | null>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pending, setPending] = useState<boolean>(false);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    async function handleClose(willPostpone: boolean): Promise<void> {

        if (willPostpone) {

            if (postponedToDate === null || postponedToDate < new Date()) {
                setErrorMessage('Can only postpone to a future date');
                return;
            }

            setPending(true);
            
            const success: boolean = await apiContext.api?.setStatusDate(props.order.orderId, 'postpone', postponedToDate, tokenContext.token) ?? false;

            setPending(false);

            if (success) {
                props.onClose(true);
            } else {
                setErrorMessage('Failed to postpone order');
            }
        } else {
            props.onClose(false);
        }
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => props.onClose(false)}
            TransitionProps={{
                onEntered: (): void => setErrorMessage(null),
            }}
        >
            <DialogTitle>Postpone Order - {(props.order.vesselCode ?? '') + '_' + (props.order.orderRef ?? '')}</DialogTitle>
            <DialogContent>
                <DateInput
                    value={postponedToDate}
                    onChange={(value: Date | null): void => setPostponedToDate(value)}
                    minWidth={Math.min(windowSize[0] * 0.6, 500)}
                    maxWidth={Math.min(windowSize[0] * 0.6, 500)}
                    dense={true}
                    labelText='Postpone to date'
                />
                <br />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.6, 300)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={async (): Promise<void> => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
                <LoadingButton 
                    width='90px'
                    height='32px'
                    text='Postpone'
                    controller={pending}
                    onClick={async (): Promise<void> => handleClose(true)}
                    buttonColor='primary'
                    circularColor='error'
                    circularSize='30px'
                    variant='contained'
                />
            </DialogActions>
        </Dialog>
    );
}

export default PostponeOrderDialog;
